/**
 * Date manipulation utilities.
 * For date formatting, see format-datetime.ts
 */

import {
  addDays,
  addMonths,
  addYears,
  format,
  isSameMonth,
  startOfQuarter,
  startOfWeek,
  subDays,
} from 'date-fns';
import type { Period } from '@/entities/types';

export function getWeekStartAt(date: Date) {
  const WEEK_STARTS_ON_MONDAY = 1;
  return startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON_MONDAY });
}

export function getQuarterStartAt(date: Date) {
  // Classify the date by the quarter of its week (week starts on Monday)
  const weekStartOfDate = getWeekStartAt(date);
  const calendarQuarterStartAt = startOfQuarter(weekStartOfDate);
  const weekStartAt = getWeekStartAt(calendarQuarterStartAt);
  return isSameMonth(weekStartAt, calendarQuarterStartAt)
    ? weekStartAt
    : addDays(weekStartAt, 7);
}

export function getYearStartAt(date: Date): Date {
  // Classify the date by the first Monday of the calendar year being the year start date
  const year = getWeekStartAt(date).getFullYear();
  const jan1 = new Date(year, 0, 1, 0, 0, 0);
  const weekStartOfJan1 = getWeekStartAt(jan1);
  return weekStartOfJan1.getMonth() === 11
    ? addDays(weekStartOfJan1, 7)
    : weekStartOfJan1;
}

export function getYesterdayStartAt(date: Date) {
  return subDays(date, 1);
}

export function getPeriodStartAt(startAt: Date, period: Period) {
  if (startAt.getHours() !== 0) {
    throw new Error('Start date must be at midnight');
  }

  if (period === 'day') {
    return startAt;
  }
  if (period === 'week') {
    return getWeekStartAt(startAt);
  }
  if (period === 'quarter') {
    return getQuarterStartAt(startAt);
  }

  return getYearStartAt(startAt);
}

export function getPeriodEndAt(startAt: Date, period: Period) {
  if (startAt.getHours() !== 0) {
    throw new Error('Start date must be at midnight');
  }
  if (period === 'day') {
    return addDays(startAt, 1);
  }
  if (period === 'week') {
    return addDays(startAt, 7);
  }
  if (period === 'quarter') {
    // Add 3 months and 15 days to ensure we're well into the next quarter
    const approxNextQuarter = addDays(addMonths(startAt, 3), 15);
    return getQuarterStartAt(approxNextQuarter);
  }

  return getYearStartAt(addDays(addYears(startAt, 1), 15));
}

export function getPreviousPeriodStartAt(
  targetStartAt: Date,
  period: Period,
): Date {
  return getPeriodStartAt(subDays(targetStartAt, 1), period);
}

export function toLocalISOString(date: Date) {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
}

export function toDateTimeStringWithTimezone(date: Date = new Date()): string {
  return format(date, "yyyy-MM-dd'T'HH:mm:ssxxx");
}

export function toMidnightLocalString(date: Date): string {
  return format(date, 'yyyy-MM-dd') + 'T00:00:00';
}

export function toFullDayDateString(date: Date) {
  return format(date, 'yyyy-MM-dd');
}

export function isToday(targetStartAtLocal: string): boolean {
  const todayDate = format(new Date(), 'yyyy-MM-dd');
  return targetStartAtLocal.startsWith(todayDate);
}
